import { RichTextPreview } from '@/components/editor/rich-text-preview';
import { FormRenderer } from '@/components/shared/form';
import { Heading3, MutedText } from '@/components/ui/typography';
import { useIsPageEditor } from '@/hooks/use-is-page-editor';
import { useIsPreviewMode } from '@/hooks/use-is-preview-mode';
import { type FormBlockResolvedForm, useResolvedFormBlock } from '@/lib/blocks/form-hydration';
import { cn } from '@/lib/utils';
import { EmbedBlock } from './embed-block';
import { FORM_BLOCK_LAYOUT_CLASS_NAME } from './form-block-layout';

export type FormBlockBackground = 'white' | 'transparent';
export type FormBlockHeadingAlignment = 'left' | 'center';

export const FORM_BLOCK_BACKGROUND_VALUES: readonly FormBlockBackground[] = [
  'white',
  'transparent',
];
export const FORM_BLOCK_HEADING_ALIGNMENT_VALUES: readonly FormBlockHeadingAlignment[] = [
  'left',
  'center',
];

// One fixed border colour cannot show up against both a light and a dark form
// background, so the border takes some of its colour from the text on whichever
// surface it sits on and stays visible either way.
export const SURFACE_DERIVED_INPUT_BORDER =
  '[--input:color-mix(in_srgb,var(--foreground)_35%,var(--background))]';
export const CARD_SURFACE_DERIVED_INPUT_BORDER =
  '[--input:color-mix(in_srgb,var(--card-foreground)_35%,var(--card))]';

const NON_TRANSPARENT_FIELD_CLASSNAMES = [
  '[&_label]:text-foreground [&_label_span]:text-foreground',
  '[&_[data-slot=form-field-helper]]:text-muted-foreground',
];

const TRANSPARENT_FIELD_CLASSNAMES = [
  SURFACE_DERIVED_INPUT_BORDER,
  '[&[data-slot=form-description]]:text-current [&[data-slot=form-description]]:opacity-80',
  '[&_a]:!text-current [&_a:hover]:!text-current',
  '[&_label]:text-current [&_label_span]:text-current',
  '[&_[data-slot=form-field-helper]]:text-current [&_[data-slot=form-field-helper]]:opacity-80',
  '[&_[data-slot=input]]:bg-background [&_[data-slot=input]]:text-foreground [&_[data-slot=input]]:border-input [&_[data-slot=input]]:shadow-sm',
  '[&_[data-slot=input-group]]:bg-background [&_[data-slot=input-group]]:border-input [&_[data-slot=input-group]]:shadow-sm',
  '[&_[data-slot=input-group-control]]:bg-background [&_[data-slot=input-group-control]]:text-foreground [&_[data-slot=input-group-control]]:border-input',
  '[&_[data-slot=textarea]]:bg-background [&_[data-slot=textarea]]:text-foreground [&_[data-slot=textarea]]:border-input [&_[data-slot=textarea]]:shadow-sm',
  '[&_[role=combobox]]:bg-background [&_[role=combobox]]:text-foreground [&_[role=combobox]]:border-input [&_[role=combobox]]:shadow-sm',
  '[&_input[aria-hidden=true]]:!opacity-0 [&_input[aria-hidden=true]]:!pointer-events-none',
  '[&_[data-slot=radio-group-item]]:border-current [&_[data-slot=radio-group-item]]:bg-transparent [&_[data-slot=radio-group-item]]:text-current',
  '[&_[data-slot=radio-group-indicator]_svg]:fill-current [&_[data-slot=radio-group-indicator]_svg]:text-current',
  '[&_[data-slot=checkbox]]:border-current [&_[data-slot=checkbox]]:bg-transparent [&_[data-slot=checkbox]]:text-current',
  '[&_[data-slot=checkbox][data-state=checked]]:border-current [&_[data-slot=checkbox][data-state=checked]]:bg-transparent [&_[data-slot=checkbox][data-state=checked]]:text-current',
  '[&_[data-slot=checkbox][data-state=indeterminate]]:border-current [&_[data-slot=checkbox][data-state=indeterminate]]:bg-transparent [&_[data-slot=checkbox][data-state=indeterminate]]:text-current',
  '[&_[data-slot=checkbox-indicator]_svg]:text-current',
];

export interface FormBlockProps {
  form?: FormBlockResolvedForm;
  formId?: string | null;
  submitLabel?: string;
  background?: FormBlockBackground;
  headingAlignment?: FormBlockHeadingAlignment;
}

export function FormBlock({
  form,
  formId,
  submitLabel,
  background = 'white',
  headingAlignment = 'left',
}: FormBlockProps) {
  const isPageEditor = useIsPageEditor();
  const isPreviewMode = useIsPreviewMode();
  const resolvedForm = useResolvedFormBlock({ form, formId: formId ?? form?.id });
  const displayTitle = resolvedForm?.title;
  const displayDescription = resolvedForm?.description;
  const thirdPartyForm = resolvedForm?.settings?.thirdPartyForm;
  const thirdPartyEmbedCode =
    resolvedForm?.thirdParty || thirdPartyForm?.enabled
      ? thirdPartyForm?.embedCode?.trim()
      : undefined;

  const isTransparent = background === 'transparent';
  const isCentered = headingAlignment === 'center';
  const isCMSContext = isPageEditor || isPreviewMode;

  if (!resolvedForm && !isCMSContext) {
    return <div className="w-full" />;
  }

  const content = (
    <div className="space-y-4">
      {displayTitle ? (
        <Heading3
          className={cn(
            isTransparent ? 'text-current' : 'text-foreground',
            isCentered && 'text-center',
          )}
        >
          {displayTitle}
        </Heading3>
      ) : null}
      {displayDescription ? (
        <RichTextPreview
          data-slot="form-description"
          mode="trusted"
          value={displayDescription}
          className={cn(
            isTransparent ? TRANSPARENT_FIELD_CLASSNAMES : 'text-muted-foreground',
            isCentered && 'text-center',
          )}
        />
      ) : null}

      <div
        className={cn(
          isTransparent ? TRANSPARENT_FIELD_CLASSNAMES : NON_TRANSPARENT_FIELD_CLASSNAMES,
        )}
      >
        {thirdPartyEmbedCode ? (
          <EmbedBlock html={thirdPartyEmbedCode} />
        ) : (
          <FormRenderer
            form={resolvedForm}
            submitLabel={submitLabel}
            fallback={<MutedText className="text-sm">Form not available.</MutedText>}
          />
        )}
      </div>
    </div>
  );

  return (
    <div className={FORM_BLOCK_LAYOUT_CLASS_NAME}>
      {isTransparent ? (
        content
      ) : (
        <div
          className={cn(
            'rounded-xl bg-card p-8 text-card-foreground shadow-md',
            CARD_SURFACE_DERIVED_INPUT_BORDER,
          )}
        >
          {content}
        </div>
      )}
    </div>
  );
}
